// --- React
import React from 'react';
// --- Hooks
import { useDialogsActions } from '../hooks';
// --- Forms
import { useDialogsForm } from '../forms';
// --- UI Components
import { FileUploader } from '@dtci/spellbook/FileUploader';
import Button from '@dtci/spellbook/Button';
import { saveAs } from 'file-saver';

/**
 * AttachmentUploadForm Component
 *
 * This component is responsible for rendering the file upload form.
 * It uses the FileUploader component to allow users to upload multiple files.
 * It handles file upload events and updates the form state accordingly.
 *
 * @component
 * @returns {JSX.Element} The rendered AttachmentUploadForm component.
 */
const AttachmentUploadForm = () => {
    const form = useDialogsForm();

    // Function to handle file upload
    const fileUploadHandler = async (fileList, rawFiles) => {
        try {
            let rawFile = [];
            // Filter the rawFiles array to find files that are new and match the names in fileList
            for (let i = 0; i < rawFiles.length; i++) {
                const file = rawFiles[i];
                // Check if the file is new and matches the names in fileList
                if (
                    fileList.find(
                        (currentFile) => file.name === currentFile.name && currentFile.new,
                    )
                ) {
                    // If the file is new, add it to the rawFile array
                    rawFile.push(file);
                }
            }

            // Set the form value for 'file' to the filtered rawFile array
            form.setValue('file', rawFile);
        } catch (error) {
            throw new Error('Function not implemented.');
        }
    };

    const handleDownloadTemplate = () => {
        fetch('/files/DateMathBulkTemplate.xlsx')
            .then((response) => response.blob())
            .then((blob) => {
                saveAs(blob, 'DateMathBulkTemplate.xlsx');
            })
            .catch((error) => {
                console.error('Download failed:', error);
            });
    };

    const handleDownloadSample = () => {
        fetch('/files/DateMathBulkSample.xlsx')
            .then((response) => response.blob())
            .then((blob) => {
                saveAs(blob, 'DateMathBulkSample.xlsx');
            })
            .catch((error) => {
                console.error('Download failed:', error);
            });
    };

    return (
       <div>
           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
               <div>
                   <Button type="button" onClick={handleDownloadTemplate} styleType="link">
                       DOWNLOAD TEMPLATE
                   </Button>
               </div>
               <div>
                   <Button type="button" onClick={handleDownloadSample} styleType="link">
                       DOWNLOAD SAMPLE
                   </Button>
               </div>
           </div>
           <FileUploader
               deleteFileConfirmationModal={{
                   text: 'Are you sure you want to delete this file?',
                   title: 'Delete File',
               }}
               displayFiles="list"
               labelText="Choose File"
               value={form.state.formData?.file}
               handleFileUpload={fileUploadHandler}
           />
       </div>
    );
};

/**
 * useUploadDialog Hook
 *
 * This hook provides functionality to open the upload dialog for attachments.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openUploadDialog function.
 */
export const useUploadDialog = () => {
    const { openDialog, closeDialog } = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = (onSubmit) => () => {
        onSubmit(form.state.formData);
        form.reset();
        closeDialog();
    };

    // Function to open the upload dialog
    const openUploadDialog = (onConfirm) =>
        // Open the upload dialog with the provided data
        openDialog({
            title: `Upload Date Math`,
            content: <AttachmentUploadForm />,
            closeAction: true,
            primaryAction: {
                label: 'Upload',
                action: handleSubmit(onConfirm),
            },
        });

    return { openUploadDialog };
};
